/**
 * IMPORTANT: Most of the functionality in this file has been replaced by the useApi hook.
 * 
 * The only functions actively used from this file are:
 * - getActivityLogs: Used by the ActivityLogs component
 * - createTestLog: Used by the ActivityLogs component for test log creation
 * 
 * For hotel-related operations, please use the useApi hook from src/hooks/useApi.js instead,
 * which provides proper caching and state management.
 * 
 * This file is retained for compatibility with existing components that still use these functions.
 */

import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  getDoc, 
  setDoc,
  orderBy,
  limit,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Cache for activity logs
let logsCache = {
  data: null,
  timestamp: null,
  expiryTime: 5 * 60 * 1000 // 5 minutes in milliseconds
};

// Hotel functions
export const getAllHotels = async () => {
  const hotelsSnapshot = await getDocs(collection(db, 'hotels'));
  const hotels = [];
  
  hotelsSnapshot.forEach((doc) => {
    hotels.push({
      id: doc.id,
      ...doc.data(),
    });
  });
  
  return hotels;
};

export const updateHotel = async (id, updates) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }

  console.log('⭐ updateHotel called:', { id, updates });

  try {
    // Get user role
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    const userData = userDoc.data();

    if (!userData || !['admin', 'editor', 'SOS'].includes(userData.role)) {
      throw new Error('Insufficient permissions');
    }

    // Get current hotel data
    const hotelDoc = await getDoc(doc(db, 'hotels', id));
    if (!hotelDoc.exists()) {
      throw new Error('Hotel not found');
    }
    const currentHotel = hotelDoc.data();

    // If user is editor or SOS, only allow updating specific fields
    let updateData = updates;
    if (userData.role === 'editor' || userData.role === 'SOS') {
      const allowedFields = ['notes', 'tags', 'address', 'phone', 'email'];
      updateData = Object.keys(updates)
        .filter((key) => allowedFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = updates[key];
          return obj;
        }, {});
    }

    // Determine what fields have changed
    const changedFields = getChangedFields(currentHotel, updateData);
    console.log('⭐ Detected changed fields:', changedFields);

    // Merge category data
    const finalUpdate = {
      ...updateData,
      categoryData: {
        ...currentHotel.categoryData,
        ...updateData.categoryData,
      },
      updatedAt: new Date(),
      updatedBy: auth.currentUser.email,
    };

    console.log('⭐ About to update hotel doc with:', finalUpdate);
    
    // Update the hotel document
    await updateDoc(doc(db, 'hotels', id), finalUpdate);
    console.log('⭐ Hotel document updated successfully, now attempting to log activity');
    
    // Log the activity
    try {
      // Ensure we have changedFields to log
      if (!changedFields || changedFields.length === 0) {
        console.log('⭐ No changes detected to log, skipping activity log creation');
        return { success: true, message: 'Hotel updated successfully (no changes to log)' };
      }
      
      console.log('⭐ Creating activity log with:', {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        userRole: userData.role,
        hotelName: currentHotel.categoryData?.category_name?.value || 'Unknown Hotel',
        hotelPropertyNumber: currentHotel.categoryData?.category_propertynumber?.value || id,
        changedFields: changedFields
      });
      
      // Prepare the data for the log entry
      const logData = {
        timestamp: serverTimestamp(),
        user: {
          id: auth.currentUser.uid,
          email: auth.currentUser.email,
          role: userData.role
        },
        action: 'HOTEL_UPDATED',
        entityId: id,
        entityType: 'hotel',
        details: {
          hotelName: currentHotel.categoryData?.category_name?.value || 'Unknown Hotel',
          hotelPropertyNumber: currentHotel.categoryData?.category_propertynumber?.value || id,
          changedFields: changedFields
        }
      };
      
      console.log('⭐ Log data prepared:', logData);
      
      // Create the document in Firestore
      console.log('⭐ Adding document to activityLogs collection...');
      const logRef = await addDoc(collection(db, 'activityLogs'), logData);
      
      console.log('⭐ Activity log created successfully with ID:', logRef.id);
    } catch (logError) {
      console.error('⭐❌ Failed to create activity log:', logError);
      // Print detailed error information for debugging
      console.error('Error details:', logError.code, logError.message);
      console.error('Error stack:', logError.stack);
      
      // Try to identify permissions issues
      if (logError.code === 'permission-denied') {
        console.error('⭐❌ Permission denied error. Make sure the user has write access to the activityLogs collection');
      }
      
      // Try to identify structure issues
      try {
        // Check if we can create a simpler log to isolate the issue
        console.log('⭐ Attempting to create simplified test log...');
        const simpleLogRef = await addDoc(collection(db, 'activityLogs'), {
          timestamp: serverTimestamp(),
          user: { id: auth.currentUser.uid },
          action: 'DEBUG_TEST',
          entityId: id,
          details: { test: true }
        });
        console.log('⭐ Simple test log created with ID:', simpleLogRef.id);
        console.error('⭐❌ Original log failed but simple log succeeded - likely a data structure issue');
      } catch (simplifyError) {
        console.error('⭐❌ Simple test log also failed:', simplifyError);
        console.error('⭐❌ This suggests a permissions or database connection issue, not data structure');
      }
      
      // Don't fail the whole operation if logging fails
    }

    return { success: true, message: 'Hotel updated successfully' };
  } catch (error) {
    console.error('⭐❌ Error in updateHotel:', error);
    throw error;
  }
};

export const searchHotels = async (searchQuery, field) => {
  try {
    if (field && searchQuery) {
      // Search by specific field
      const q = query(
        collection(db, 'hotels'),
        where(field, '>=', searchQuery),
        where(field, '<=', searchQuery + '\uf8ff')
      );
      const snapshot = await getDocs(q);
      
      const hotels = [];
      snapshot.forEach((doc) => {
        hotels.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      
      return hotels;
    } else if (searchQuery) {
      // Search by name and email
      const nameQuery = query(
        collection(db, 'hotels'),
        where('name', '>=', searchQuery),
        where('name', '<=', searchQuery + '\uf8ff')
      );
      
      const emailQuery = query(
        collection(db, 'hotels'),
        where('email', '>=', searchQuery),
        where('email', '<=', searchQuery + '\uf8ff')
      );
      
      const [nameSnapshot, emailSnapshot] = await Promise.all([
        getDocs(nameQuery),
        getDocs(emailQuery)
      ]);
      
      const results = new Map();
      
      nameSnapshot.forEach((doc) => {
        results.set(doc.id, { id: doc.id, ...doc.data() });
      });
      
      emailSnapshot.forEach((doc) => {
        results.set(doc.id, { id: doc.id, ...doc.data() });
      });
      
      return Array.from(results.values());
    } else {
      // Return all hotels
      return await getAllHotels();
    }
  } catch (error) {
    throw error;
  }
};

// Category functions
export const getCategories = async () => {
  const categoriesSnapshot = await getDocs(collection(db, 'categories'));
  const categories = [];
  
  categoriesSnapshot.forEach((doc) => {
    categories.push({
      id: doc.id,
      ...doc.data(),
    });
  });
  
  return categories;
};

export const updateCategories = async (categories) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }

  try {
    // Get user role
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    const userData = userDoc.data();

    if (!userData || userData.role !== 'admin') {
      throw new Error('Only admins can update categories');
    }

    // Update categories document
    await setDoc(doc(db, 'categories', 'main'), { 
      list: categories,
      updatedAt: new Date(),
      updatedBy: auth.currentUser.email
    });
    
    return { message: 'Categories updated successfully' };
  } catch (error) {
    throw error;
  }
};

// User functions
export const getCurrentUser = async () => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }

  try {
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (userDoc.exists()) {
      return { ...userDoc.data(), uid: auth.currentUser.uid };
    } else {
      return { 
        email: auth.currentUser.email,
        displayName: auth.currentUser.displayName,
        photoURL: auth.currentUser.photoURL,
        uid: auth.currentUser.uid
      };
    }
  } catch (error) {
    throw error;
  }
};

export const updateUserProfile = async (updates) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }

  try {
    await updateDoc(doc(db, 'users', auth.currentUser.uid), {
      ...updates,
      updatedAt: new Date()
    });
    
    return { message: 'Profile updated successfully' };
  } catch (error) {
    throw error;
  }
};

export const getAuthorizedUsers = async () => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }

  try {
    // Get user role
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    const userData = userDoc.data();

    if (!userData || userData.role !== 'admin') {
      throw new Error('Only admins can view authorized users');
    }

    const usersSnapshot = await getDocs(collection(db, 'authorized_users'));
    const users = [];
    
    usersSnapshot.forEach((doc) => {
      users.push({
        email: doc.id,
        ...doc.data(),
      });
    });
    
    return users;
  } catch (error) {
    throw error;
  }
};

export const authorizeUser = async (email, role) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }

  try {
    // Get user role
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    const userData = userDoc.data();

    if (!userData || userData.role !== 'admin') {
      throw new Error('Only admins can authorize users');
    }

    await setDoc(doc(db, 'authorized_users', email.toLowerCase()), {
      role,
      addedAt: new Date(),
      addedBy: auth.currentUser.email
    });
    
    return { message: 'User authorized successfully' };
  } catch (error) {
    throw error;
  }
};

export const removeAuthorization = async (email) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }

  try {
    // Get user role
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    const userData = userDoc.data();

    if (!userData || userData.role !== 'admin') {
      throw new Error('Only admins can remove user authorization');
    }

    // Don't allow removing your own admin access
    if (email.toLowerCase() === auth.currentUser.email.toLowerCase()) {
      throw new Error('You cannot remove your own admin access');
    }

    // Protect system creator from being removed
    if (email.toLowerCase().includes('jesper.vestin')) {
      throw new Error('Cannot remove the system creator');
    }
    
    await setDoc(doc(db, 'authorized_users', email.toLowerCase()), {
      removed: true,
      removedAt: new Date(),
      removedBy: auth.currentUser.email
    }, { merge: true });
    
    return { message: 'User authorization removed successfully' };
  } catch (error) {
    throw error;
  }
};

// Helper function to determine what fields have changed
const getChangedFields = (original, updates) => {
  const changedFields = [];
  try {
    console.log('⭐ CHANGE DETECTION - Original (stringified):', JSON.stringify(original, null, 2).substring(0, 1000) + '...');
    console.log('⭐ CHANGE DETECTION - Updates (stringified):', JSON.stringify(updates, null, 2).substring(0, 1000) + '...');
  } catch (e) {
    console.log('⭐ Error stringifying objects:', e);
  }
  console.log('⭐ CHANGE DETECTION - Original:', original);
  console.log('⭐ CHANGE DETECTION - Updates:', updates);
  
  // Check direct properties
  Object.keys(updates).forEach(key => {
    // Special handling for notes array to compare content instead of references
    if (key === 'notes' && Array.isArray(original[key]) && Array.isArray(updates[key])) {
      // Extract just the content from notes
      const originalContents = original[key].map(note => note.content).filter(Boolean);
      const updatedContents = updates[key].map(note => note.content).filter(Boolean);
      
      // Check if content is different (ignoring other properties like timestamps)
      const contentChanged = JSON.stringify(originalContents) !== JSON.stringify(updatedContents);
      console.log('⭐ Notes comparison:', { 
        originalContents, 
        updatedContents, 
        contentChanged 
      });
      
      if (contentChanged) {
        changedFields.push(key);
        console.log(`⭐ Notes changed: content is different`);
      } else {
        console.log(`⭐ Notes unchanged: content is the same, ignoring metadata changes`);
      }
    } 
    // Normal handling for other fields
    else if (key !== 'categoryData' && JSON.stringify(original[key]) !== JSON.stringify(updates[key])) {
      changedFields.push(key);
      console.log(`⭐ Direct property changed: ${key}`);
    }
  });
  
  // Check categoryData
  if (updates.categoryData && original.categoryData) {
    Object.keys(updates.categoryData).forEach(key => {
      // Extract field name without 'category_' prefix for logging
      const displayFieldName = key.startsWith('category_') ? key.substring(9) : key;
      
      // Compare the values - but just extract the value property since updateAt will always be different
      const originalValue = original.categoryData[key]?.value;
      const updatedValue = updates.categoryData[key]?.value;
      
      console.log(`⭐ Comparing category field ${displayFieldName}:`, {
        original: originalValue,
        updated: updatedValue,
        changed: JSON.stringify(originalValue) !== JSON.stringify(updatedValue)
      });
      
      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        // Extract field name without 'category_' prefix for the changedFields array
        const fieldName = key.startsWith('category_') ? key.substring(9) : key;
        changedFields.push(fieldName);
        console.log(`⭐ Category field changed: ${displayFieldName}, from "${originalValue}" to "${updatedValue}"`);
      }
    });
  }
  
  // If there are no detected changes but categoryData exists in updates, 
  // it might be that we're just updating a single field through handleEditFieldChange
  if (changedFields.length === 0 && updates.categoryData) {
    console.log('⭐ No direct changes detected, checking for field edits in categoryData');
    
    // Try to extract changed field from the categoryData structure
    // This handles the case when a single category field is updated through handleEditFieldChange
    Object.keys(updates.categoryData).forEach(key => {
      // We know this key is in updates, so consider it changed unless it's exactly the same
      const fieldName = key.startsWith('category_') ? key.substring(9) : key;
      
      // If this field is new or different from original, add it
      if (!original.categoryData[key] || 
          original.categoryData[key].value !== updates.categoryData[key].value) {
        // Only add if not already in the list
        if (!changedFields.includes(fieldName)) {
          changedFields.push(fieldName);
          console.log(`⭐ Detected edit in field: ${fieldName}`);
        }
      }
    });
  }
  
  console.log('⭐ Final changed fields:', changedFields);
  return changedFields;
};

// Activity Logs functions
export const getActivityLogs = async (limitCount = 100, forceRefresh = false) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }

  // Use cache if available and not expired, unless forceRefresh is true
  const now = new Date().getTime();
  if (
    !forceRefresh && 
    logsCache.data && 
    logsCache.timestamp && 
    (now - logsCache.timestamp) < logsCache.expiryTime
  ) {
    console.log('Returning cached activity logs data');
    return logsCache.data;
  }

  try {
    // Get user role
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    const userData = userDoc.data();

    if (!userData || userData.role !== 'admin') {
      throw new Error('Only admins can view activity logs');
    }

    // Query activity logs
    const q = query(
      collection(db, 'activityLogs'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    console.log('Fetching activity logs from Firestore');
    const snapshot = await getDocs(q);
    const logs = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      logs.push({
        id: doc.id,
        ...data,
        // Convert server timestamp to JS Date if it exists
        timestamp: data.timestamp ? data.timestamp.toDate() : new Date()
      });
    });
    
    // Update cache
    logsCache = {
      data: logs,
      timestamp: now,
      expiryTime: 5 * 60 * 1000
    };
    
    return logs;
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    throw error;
  }
};

// Clear logs cache - can be called when new logs are created
export const clearLogsCache = () => {
  logsCache.data = null;
  logsCache.timestamp = null;
  console.log('Activity logs cache cleared');
};

// Test function to create a log entry directly
export const createTestLog = async () => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }

  try {
    // Get user role
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    const userData = userDoc.data();

    if (!userData || userData.role !== 'admin') {
      throw new Error('Only admins can create test logs');
    }

    // Generate random test data for variety
    const timestamp = new Date();
    const timestampString = timestamp.toISOString();
    
    const logRef = await addDoc(collection(db, 'activityLogs'), {
      timestamp: serverTimestamp(),
      user: {
        id: auth.currentUser.uid,
        email: auth.currentUser.email,
        role: userData.role
      },
      action: 'TEST_LOG',
      entityId: 'test-id-' + timestamp.getTime(),
      entityType: 'test',
      details: {
        message: 'This is a test log entry created at ' + timestampString,
        created: timestampString,
        hotelName: 'Test Hotel ' + Math.floor(Math.random() * 100),
        changedFields: ['text', 'boolean', 'number', 'array', 'object', 'null', 'date', 'long-text'],
        changes: [
          {
            field: 'text',
            from: 'Previous text value',
            to: 'New text value with test data'
          },
          {
            field: 'boolean',
            from: false,
            to: true
          },
          {
            field: 'number',
            from: 100,
            to: 250
          },
          {
            field: 'array',
            from: ['item1', 'item2'],
            to: ['item1', 'item2', 'item3', 'item4']
          },
          {
            field: 'object',
            from: { key1: 'value1' },
            to: { key1: 'value1', key2: 'value2', nested: { deep: true } }
          },
          {
            field: 'null',
            from: null,
            to: 'Now has a value'
          },
          {
            field: 'date',
            from: '2023-01-01T12:00:00.000Z',
            to: timestampString
          },
          {
            field: 'long-text',
            from: 'Short description',
            to: 'This is a much longer text value that should test how the UI handles longer content. It includes multiple sentences and should wrap properly in the UI. This helps ensure that text fields with substantial content display correctly.'
          }
        ]
      }
    });
    
    // Clear cache after creating a new log
    clearLogsCache();
    
    console.log('Test log created successfully with ID:', logRef.id);
    return { success: true, logId: logRef.id };
  } catch (error) {
    console.error('Failed to create test log:', error);
    throw error;
  }
}; 